import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Gamepad2, Mail, Lock, User, ArrowLeft, Loader2 } from "lucide-react";

function AuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signup, login, loginWithGoogle, resetPassword, currentUser } = useAuth();

  const [mode, setMode] = useState(searchParams.get("mode") || "login");
  const [role, setRole] = useState(searchParams.get("role") || "influencer");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");

  useEffect(() => {
    if (currentUser) {
      navigate("/dashboard");
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    const m = searchParams.get("mode");
    const r = searchParams.get("role");
    if (m) setMode(m);
    if (r) setRole(r);
  }, [searchParams]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (mode === "signup") {
        if (!displayName.trim()) {
          setError("Please enter your name.");
          setLoading(false);
          return;
        }
        if (role === "influencer" && !username.trim()) {
          setError("Please enter a username.");
          setLoading(false);
          return;
        }
        if (!acceptedTerms) {
          setError("You must accept the Terms & Conditions to sign up.");
          setLoading(false);
          return;
        }
        await signup(email, password, displayName, role, username.trim());
      } else {
        await login(email, password);
      }
      navigate("/dashboard");
    } catch (err) {
      const msg = err.code === "auth/email-already-in-use"
        ? "An account with this email already exists."
        : err.code === "auth/invalid-credential"
        ? "Invalid email or password."
        : err.code === "auth/weak-password"
        ? "Password should be at least 6 characters."
        : err.code === "auth/invalid-email"
        ? "Please enter a valid email address."
        : err.message;
      setError(msg);
    }
    setLoading(false);
  }

  async function handleGoogleSignIn() {
    setError("");
    setLoading(true);
    try {
      await loginWithGoogle(role);
      navigate("/dashboard");
    } catch (err) {
      setError("Google sign-in failed. Please try again.");
    }
    setLoading(false);
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    setError("");
    setResetSuccess("");
    setLoading(true);
    try {
      await resetPassword(resetEmail);
      setResetSuccess("Password reset email sent! Check your inbox.");
    } catch (err) {
      const msg = err.code === "auth/user-not-found"
        ? "No account found with this email."
        : err.code === "auth/invalid-email"
        ? "Please enter a valid email address."
        : err.message;
      setError(msg);
    }
    setLoading(false);
  }

  const isSignup = mode === "signup";

  return (
    <div className="min-h-screen bg-gray-950 text-white flex">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-brand-900 via-gray-900 to-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-brand-500/10 via-transparent to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/" className="flex items-center gap-2">
            <Gamepad2 className="w-8 h-8 text-brand-400" />
            <span className="text-xl font-bold bg-gradient-to-r from-brand-400 to-purple-400 bg-clip-text text-transparent">
              referred.games
            </span>
          </Link>
          <div>
            <h2 className="text-4xl font-bold mb-4 leading-tight">
              {isSignup
                ? "Join the marketplace that connects games with influencers"
                : "Welcome back to referred.games"}
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed">
              {isSignup
                ? "Whether you're a publisher looking for exposure or an influencer looking to earn — we've got you covered."
                : "Sign in to manage your campaigns, track your earnings, and grow your reach."}
            </p>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <span>Performance-based</span>
            <span className="w-1 h-1 rounded-full bg-gray-700" />
            <span>Steam UTM tracking</span>
            <span className="w-1 h-1 rounded-full bg-gray-700" />
            <span>Transparent analytics</span>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Gamepad2 className="w-8 h-8 text-brand-400" />
            <span className="text-xl font-bold bg-gradient-to-r from-brand-400 to-purple-400 bg-clip-text text-transparent">
              referred.games
            </span>
          </div>

          <h1 className="text-3xl font-bold mb-2">
            {resetMode ? "Reset your password" : isSignup ? "Create your account" : "Sign in to your account"}
          </h1>
          <p className="text-gray-400 mb-8">
            {resetMode ? (
              <>
                Remember your password?{" "}
                <button
                  onClick={() => { setResetMode(false); setError(""); setResetSuccess(""); }}
                  className="text-brand-400 hover:text-brand-300 font-medium"
                >
                  Sign in
                </button>
              </>
            ) : isSignup ? (
              <>
                Already have an account?{" "}
                <button
                  onClick={() => setMode("login")}
                  className="text-brand-400 hover:text-brand-300 font-medium"
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                Don't have an account?{" "}
                <button
                  onClick={() => setMode("signup")}
                  className="text-brand-400 hover:text-brand-300 font-medium"
                >
                  Sign up
                </button>
              </>
            )}
          </p>

          {/* Role selector - only on signup */}
          {/* {isSignup && (
            <div className="flex gap-3 mb-6">
              <button
                onClick={() => setRole("publisher")}
                className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all border ${
                  role === "publisher"
                    ? "bg-blue-500/10 border-blue-500/30 text-blue-400"
                    : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700"
                }`}
              >
                <Gamepad2 className="w-5 h-5 mx-auto mb-1" />
                Publisher
              </button>
              <button
                onClick={() => setRole("influencer")}
                className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all border ${
                  role === "influencer"
                    ? "bg-purple-500/10 border-purple-500/30 text-purple-400"
                    : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700"
                }`}
              >
                <User className="w-5 h-5 mx-auto mb-1" />
                Influencer
              </button>
            </div>
          )} */}

          {/* Google Sign In */}
          {/* <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-white/5 hover:bg-white/10 border border-gray-800 hover:border-gray-700 text-white py-3 px-4 rounded-xl transition-all font-medium mb-6"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button> */}

          {/* <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gray-800" />
            <span className="text-sm text-gray-500">or</span>
            <div className="flex-1 h-px bg-gray-800" />
          </div> */}

          {/* Email/Password Form */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3 mb-6">
              {error}
            </div>
          )}

          {resetSuccess && (
            <div className="bg-green-500/10 border border-green-500/20 text-green-400 text-sm rounded-xl px-4 py-3 mb-6">
              {resetSuccess}
            </div>
          )}

          {resetMode ? (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-600 hover:bg-brand-500 text-white py-3 px-4 rounded-xl transition-all font-semibold flex items-center justify-center gap-2 shadow-lg shadow-brand-600/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Send Reset Link"
                )}
              </button>
            </form>
          ) : (
          <>

          {/* Role selector - only on signup */}
          {isSignup && (
            <div className="flex gap-3 mb-6">
              <button
                onClick={() => setRole("publisher")}
                className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all border ${
                  role === "publisher"
                    ? "bg-blue-500/30 border-blue-500/30 text-blue-400"
                    : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700"
                }`}
              >
                <Gamepad2 className="w-5 h-5 mx-auto mb-1" />
                Publisher
              </button>
              <button
                onClick={() => setRole("influencer")}
                className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all border ${
                  role === "influencer"
                    ? "bg-purple-500/30 border-purple-500/30 text-purple-400"
                    : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700"
                }`}
              >
                <User className="w-5 h-5 mx-auto mb-1" />
                Influencer
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignup && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
                  />
                </div>
              </div>
            )}

            {isSignup && role === "influencer" && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  Username
                </label>
                <p className="text-xs text-gray-500 mb-1.5">
                  The name you're known by across your platforms (no spaces). This will be used in your UTM links.
                </p>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
                    placeholder="e.g. NinjaGamer, xQc"
                    className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
                />
              </div>
            </div>

            {isSignup && (
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-gray-600 text-brand-500 focus:ring-brand-500 bg-gray-700 flex-shrink-0"
                />
                <span className="text-sm text-gray-400">
                  I agree to the{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-400 hover:text-brand-300 underline"
                  >
                    Terms & Conditions
                  </a>
                </span>
              </label>
            )}

            <button
              type="submit"
              disabled={loading || (isSignup && !acceptedTerms)}
              className="w-full bg-brand-600 hover:bg-brand-500 text-white py-3 px-4 rounded-xl transition-all font-semibold flex items-center justify-center gap-2 shadow-lg shadow-brand-600/25 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isSignup ? (
                "Create Account"
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          {!isSignup && (
            <div className="mt-4 text-center">
              <button
                onClick={() => { setResetMode(true); setError(""); setResetSuccess(""); }}
                className="text-sm text-gray-400 hover:text-brand-400 transition-colors"
              >
                Forgot your password?
              </button>
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
